import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import type {
	BuilderComponentModule,
	ModuleEntries,
	IBuilderComponentLoader,
	BuilderComponentSiteConfig,
	IBuilderComponentPageConfig,
	AnyReactComponent,
} from './types'
import type { ISessionManager, Experiments, ILayoutDoneService, ModuleLoader, ILogger } from '@wix/thunderbolt-symbols'
import {
	ManifestManagerApiSymbol,
	SiteFeatureConfigSymbol,
	PageFeatureConfigSymbol,
	ExperimentsSymbol,
	LayoutDoneServiceSymbol,
	LoggerSymbol,
} from '@wix/thunderbolt-symbols'
import { name } from './symbols'
import { SessionManagerSymbol } from 'feature-session-manager'
import type { ManifestManagerApi } from '@wix/thunderbolt-becky-types'
import { createPolyfillDataWithComponentId } from './utils'
import { WrapBuilderComponent } from './componentWrapper'
import { ESMLoaderSymbol } from 'feature-builder-module-loader'
import LazyBuilderComponentDeadComp from './components/LazyBuilderComponentDeadComp'

export const builderComponentLoader = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		named(PageFeatureConfigSymbol, name),
		SessionManagerSymbol,
		ExperimentsSymbol,
		ESMLoaderSymbol,
		optional(ManifestManagerApiSymbol),
		optional(LayoutDoneServiceSymbol),
		optional(LoggerSymbol),
	] as const,
	(
		siteConfig: BuilderComponentSiteConfig,
		pageConfig: IBuilderComponentPageConfig,
		sessionManager: ISessionManager,
		experiments: Experiments,
		moduleLoader: ModuleLoader,
		manifestManagerApi: ManifestManagerApi | undefined,
		layoutDoneService: ILayoutDoneService | undefined,
		logger: ILogger | undefined
	): IBuilderComponentLoader => {
		// Create a getter function that computes polyfill data on-demand
		// This ensures we always read fresh data from configs (which may be updated byRef)
		const getPresetsPolyfillData = () => {
			return pageConfig.pagePolyfillData && siteConfig.builderComponentsData
				? createPolyfillDataWithComponentId(pageConfig.pagePolyfillData, siteConfig.builderComponentsData)
				: {}
		}

		const getComponent = async (
			componentUrl: string,
			builderType: string,
			appDefinitionId: string
		): Promise<BuilderComponentModule> => {
			const moduleEntries = await moduleLoader.loadModule<ModuleEntries>(componentUrl)
			const importKey = Object.keys(moduleEntries)[0]
			const component = moduleEntries[importKey]
			if (typeof moduleEntries.injectAccessTokenGetter === 'function') {
				const tokenGetter = sessionManager.getBoundAccessTokenFunction(appDefinitionId)
				moduleEntries.injectAccessTokenGetter(tokenGetter)
			}

			if (manifestManagerApi && moduleEntries.resolveManifestOverrides) {
				manifestManagerApi.setManifestOverridesFunc(builderType, moduleEntries.resolveManifestOverrides)
			}

			return {
				ReactComponent: component,
			}
		}

		const getComponentLoader: IBuilderComponentLoader['getComponentLoader'] = ({
			componentUrl,
			builderComponentId,
			builderType,
			appDefinitionId,
		}) => {
			return async () => {
				let loaderComponent: AnyReactComponent | undefined
				try {
					const { ReactComponent: component } = await getComponent(componentUrl, builderType, appDefinitionId)
					loaderComponent = component
				} catch (error) {
					console.warn(`Rendering DeadComp for`, builderType, 'reason:', error.message)
					if (logger) {
						logger.meter('builderDeadComp', {
							paramsOverrides: {
								evid: '26',
								errorInfo: error.message,
								errorType: 'builder_dead_comp',
								eventString: 'error',
							},
							customParams: {
								feature: 'feature-builder-component',
								builderType,
								builderComponentId,
								componentName: 'LazyBuilderComponentDeadComp',
								componentUrl,
								appDefinitionId,
							},
						})
					}
					loaderComponent = LazyBuilderComponentDeadComp
				}

				return {
					component: WrapBuilderComponent(
						loaderComponent,
						builderComponentId,
						getPresetsPolyfillData,
						layoutDoneService,
						experiments
					),
				}
			}
		}

		return {
			getComponentLoader,
		}
	}
)
